import { reportHandledException } from '@/lib/error-reporting'
/**
 * Captures the attribution signals a purchase needs for server-side conversion
 * tracking, read at payment-submit time and sent to the API in the charge
 * payload. The server persists them on the trade and replays them to Meta CAPI
 * / GA4 Measurement Protocol when the charge is confirmed — recovering 3DS
 * redirects the SPA can't confirm and ad-blocked client events.
 *
 * `eventId` is the dedup key: the browser Pixel `Purchase` fires with it AND
 * the server CAPI `Purchase` fires with the same id, so Meta counts one
 * conversion from two sources. GA4 dedups on `transaction_id` instead.
 */
export interface PurchaseAttribution {
  /** Shared client/server dedup id for this purchase attempt. */
  eventId: string
  /** Meta browser-id cookie. */
  fbp?: string
  /** Meta click-id cookie. */
  fbc?: string
  /** Parsed GA4 client id (from the `_ga` cookie). */
  gaClientId?: string
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  if (!match) return undefined
  try {
    return decodeURIComponent(match[1])
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/analytics/attribution.ts' })
    // A malformed percent-sequence in a third-party cookie must NOT throw —
    // collectPurchaseAttribution() runs before the payment submit handler's
    // try/finally, so an uncaught throw would abort the charge and leave the UI
    // stuck submitting. Treat just this field as absent (visible recovery).
    console.warn(`[analytics] failed to decode cookie ${name}:`, cause)
    return undefined
  }
}

/**
 * Extract the GA4 client id from the `_ga` cookie. The standard client-side
 * value looks like `GA1.1.1234567890.1700000000`; the client id is the last two
 * dot-separated segments (`1234567890.1700000000`), robust to the version/depth
 * prefix. Also accepts the bare `<random>.<timestamp>` form some server-side
 * GTM setups write. Returns undefined unless the last two segments are numeric,
 * so a prefix-only value like `GA1.1` is rejected rather than mis-parsed.
 */
export function parseGaClientId(gaCookie: string | undefined): string | undefined {
  if (!gaCookie) return undefined
  const parts = gaCookie.split('.')
  if (parts.length < 2) return undefined
  const id = parts.slice(-2)
  if (!/^\d+$/.test(id[0]) || !/^\d+$/.test(id[1])) return undefined
  return `${id[0]}.${id[1]}`
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID (older Safari / jsdom).
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** Build the attribution bundle for one purchase attempt (call once at submit). */
export function collectPurchaseAttribution(): PurchaseAttribution {
  return {
    eventId: generateEventId(),
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
    gaClientId: parseGaClientId(readCookie('_ga')),
  }
}
